import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss']
})
export class BreadcrumbComponent implements OnChanges {
  @Input() productName: string | null = null;
  @Input() variantName: string | null = null;

  private readonly ROUTE_LABELS: { [key: string]: string } = {
    'admin': 'Admin',
    'manage-products': 'Manage Products',
    'manage-customers': 'Manage Customers',
    'manage-categories': 'Manage Categories',
    'variant': 'Variant'
  };

  breadcrumbs: { label: string, url: string, queryParams?: any }[] = [];

  constructor(private router: Router, private route: ActivatedRoute) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.buildBreadcrumbs();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['productName'] || changes['variantName']) {
      this.buildBreadcrumbs();
    }
  }

  private buildBreadcrumbs(): void {
    const root = this.route.root;
    const raw = this.getBreadcrumbs(root);
    this.breadcrumbs = raw.map((bc, index, arr) => {
      let label = bc.label;
      let queryParams: any = {};

      if (bc.label.toLowerCase() === 'variant' && this.productName) {
        label = 'Variant';
        const prevProductId = arr[index - 1]?.label;
        if (/^\d+$/.test(prevProductId)) {
          return {
            label,
            url: `/admin/manage-products/${prevProductId}`,
            queryParams: { productName: this.productName, tab: 'variants' }
          };
        }
      }

      const isNumeric = /^\d+$/.test(bc.label);
      const prev = arr[index - 1]?.label?.toLowerCase() || '';
      const next = arr[index + 1]?.label?.toLowerCase() || '';

      if (isNumeric && prev === 'variant' && this.variantName) {
        label = this.variantName;
        queryParams.variantName = this.variantName;
        queryParams.productName = this.productName;
      } else if (isNumeric && (next === 'variant' || !next) && this.productName) {
        label = this.productName;
        queryParams.productName = this.productName;
      } else if (this.ROUTE_LABELS[bc.label.toLowerCase()]) {
        label = this.ROUTE_LABELS[bc.label.toLowerCase()];
      }

      return {
        label,
        url: bc.url,
        queryParams
      };
    });
  }

  private getBreadcrumbs(route: ActivatedRoute, parentUrl: string = '', breadcrumbs: any[] = []): any[] {
    const children: ActivatedRoute[] = route.children;
    if (children.length === 0) return breadcrumbs;

    for (const child of children) {
      const routeURL = child.snapshot.url.map(segment => segment.path).join('/');
      if (routeURL !== '') {
        const segments = routeURL.split('/');
        let cumulativeUrl = parentUrl;

        for (const segment of segments) {
          cumulativeUrl += `/${segment}`;
          breadcrumbs.push({
            label: segment,
            url: cumulativeUrl
          });
        }

        parentUrl = cumulativeUrl;
      }
      return this.getBreadcrumbs(child, parentUrl, breadcrumbs);
    }

    return breadcrumbs;
  }
}
